import type { Locale } from './index'

/**
 * Pluralization helper
 * German: 1 Tag, 2 Tage
 * English: 1 day, 2 days
 */
export function pluralize(
  count: number,
  singular: string,
  plural: string,
  locale: Locale
): string {
  if (locale === 'de') {
    return count === 1 ? singular : plural
  }
  // English and others
  return count === 1 ? singular : plural
}

/**
 * Format date for display
 * German: 01.01.2024
 * English: Jan 1, 2024
 */
export function formatDate(
  date: string | Date | undefined,
  locale: Locale
): string {
  if (!date) return ''

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return ''

  if (locale === 'de') {
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * frost-v2 (E11): kurzes Datum ohne Jahr für die Frost-Tooltip-Zeile
 * («… eingefroren seit 24.08.»).
 * German: 24.08.
 * English: Aug 24
 */
export function formatDateShort(
  date: string | Date | undefined,
  locale: Locale
): string {
  if (!date) return ''

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return ''

  if (locale === 'de') {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${dd}.${mm}.`
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format time for display
 * German: 14:30
 * English: 2:30 PM
 */
export function formatTime(
  date: string | Date | undefined,
  locale: Locale
): string {
  if (!date) return ''

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return ''

  if (locale === 'de') {
    return d.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format datetime for display
 */
export function formatDateTime(
  date: string | Date | undefined,
  locale: Locale
): string {
  if (!date) return ''

  const dateStr = formatDate(date, locale)
  const timeStr = formatTime(date, locale)

  if (!dateStr || !timeStr) return ''

  if (locale === 'de') {
    return `${dateStr}, ${timeStr} Uhr`
  }

  return `${dateStr} at ${timeStr}`
}

/**
 * Format relative time (e.g., "in 2 hours")
 * German: in 2 Stunden
 * English: in 2 hours
 */
export function formatRelativeTime(
  hours: number,
  locale: Locale
): string {
  if (locale === 'de') {
    if (hours < 1) return 'in weniger als einer Stunde'
    if (hours === 1) return 'in einer Stunde'
    if (hours < 24) return `in ${hours} Stunden`
    const days = Math.floor(hours / 24)
    if (days === 1) return 'in einem Tag'
    return `in ${days} Tagen`
  }

  if (hours < 1) return 'in less than an hour'
  if (hours === 1) return 'in an hour'
  if (hours < 24) return `in ${hours} hours`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'in a day'
  return `in ${days} days`
}

/**
 * Format relative time in the past
 * German: vor 2 Stunden
 * English: 2 hours ago
 */
export function formatTimeAgo(
  hours: number,
  locale: Locale
): string {
  if (locale === 'de') {
    if (hours < 1) return 'vor weniger als einer Stunde'
    if (hours === 1) return 'vor einer Stunde'
    if (hours < 24) return `vor ${hours} Stunden`
    const days = Math.floor(hours / 24)
    if (days === 1) return 'vor einem Tag'
    return `vor ${days} Tagen`
  }

  if (hours < 1) return 'less than an hour ago'
  if (hours === 1) return 'an hour ago'
  if (hours < 24) return `${hours} hours ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'a day ago'
  return `${days} days ago`
}

/**
 * Format number with locale-specific separators
 * German: 1.234,56
 * English: 1,234.56
 */
export function formatNumber(num: number, locale: Locale): string {
  return num.toLocaleString(locale === 'de' ? 'de-DE' : 'en-US')
}

/**
 * Format percentage
 * German: 50,5 %
 * English: 50.5%
 */
export function formatPercent(
  value: number,
  locale: Locale,
  decimals = 1
): string {
  if (locale === 'de') {
    const formatted = value.toLocaleString('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return `${formatted} %`
  }

  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return `${formatted}%`
}

/**
 * Format currency
 * German: 1.234,56 €
 * English: $1,234.56
 */
export function formatCurrency(
  amount: number,
  locale: Locale,
  currency = 'EUR'
): string {
  return amount.toLocaleString(locale === 'de' ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency,
  })
}

/**
 * Format duration in hours and minutes
 * German: 2 Stunden 30 Minuten
 * English: 2 hours 30 minutes
 */
export function formatDuration(
  minutes: number,
  locale: Locale
): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (locale === 'de') {
    if (hours === 0) {
      return pluralize(mins, '1 Minute', `${mins} Minuten`, locale)
    }
    if (mins === 0) {
      return pluralize(hours, '1 Stunde', `${hours} Stunden`, locale)
    }
    return `${hours} Std. ${mins} Min.`
  }

  if (hours === 0) {
    return pluralize(mins, '1 minute', `${mins} minutes`, locale)
  }
  if (mins === 0) {
    return pluralize(hours, '1 hour', `${hours} hours`, locale)
  }
  return `${hours}h ${mins}m`
}

/**
 * Check if text should be RTL
 * Currently only Hebrew and Arabic are RTL
 */
export function isRTLLocale(locale: Locale): boolean {
  return ['he', 'ar'].includes(locale)
}

/**
 * Get text direction
 * Returns 'rtl' for RTL locales, 'ltr' otherwise
 */
export function getTextDirection(locale: Locale): 'ltr' | 'rtl' {
  return isRTLLocale(locale) ? 'rtl' : 'ltr'
}
